import {
  useInvestmentAccountQuery,
  usePortfolioSnapshotsPageQuery,
} from '@transactions/graphql';
import {parseDate} from 'common/date';
import {ChevronLeft, ChevronRight, Pencil, Plus, Trash2} from 'lucide-react';
import * as React from 'react';
import {Navigate} from 'react-router-dom';
import {useAccountOutletContext} from 'src/account/AccountDetail';
import {Button} from 'src/Button';
import {useModals} from 'src/Modals';
import {PopoverMenu} from 'src/PopoverMenu';
import PopoverMenuItem from 'src/PopoverMenuItem';
import type {InvestmentAccount} from 'src/account/AccountDetail';

const PAGE_SIZE = 10;

/** Show previous page's data (at reduced opacity) while the next page loads. */
const useStaleWhileLoading = <T,>(items: T[], loading: boolean): T[] => {
  const prevRef = React.useRef<T[]>([]);
  React.useEffect(() => {
    if (items.length > 0) {
      prevRef.current = items;
    }
  }, [items]);
  return items.length > 0 ? items : loading ? prevRef.current : [];
};

const formatCurrency = (cents: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);

// Format an ISO date string (e.g., "2024-01-15") for display
const formatDateString = (isoDate: string): string =>
  parseDate(isoDate).toLocaleString({
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

type SnapshotItem = {
  _id: string;
  accountId: string;
  date: string;
  value: number;
  source: string;
  createdAt: number;
};

type SnapshotRowProps = {
  snapshot: SnapshotItem;
  onEdit: () => void;
  onDelete: () => void;
};

const SnapshotRow = ({snapshot, onEdit, onDelete}: SnapshotRowProps) => {
  return (
    <div className="border-border bg-card flex items-center gap-3 rounded-lg border p-4">
      <div className="flex flex-1 flex-col">
        <span className="text-foreground font-medium">
          {formatDateString(snapshot.date)}
        </span>
        <span className="text-muted-foreground text-sm">
          Source: {snapshot.source}
        </span>
      </div>
      <div className="flex flex-col items-end">
        <span className="text-foreground font-medium">
          {formatCurrency(snapshot.value)}
        </span>
      </div>
      <PopoverMenu>
        {snapshot.source === 'manual' && (
          <PopoverMenuItem iconLeft={<Pencil />} onSelect={onEdit}>
            Edit
          </PopoverMenuItem>
        )}
        <PopoverMenuItem
          iconLeft={<Trash2 className="text-destructive" />}
          onSelect={onDelete}
          className="text-destructive"
        >
          Delete
        </PopoverMenuItem>
      </PopoverMenu>
    </div>
  );
};

/**
 * Snapshots tab of the investment account detail page: the paginated list of
 * portfolio snapshots with add/edit/delete. The portfolio chart on the
 * Overview tab refreshes on its own (cache-and-network), so mutations here
 * only refetch the snapshot list and the account (whose balance and
 * snapshotCount change server-side).
 */
export const InvestmentSnapshotsView = () => {
  const {account, refetchAccount} = useAccountOutletContext();
  // The tab is only offered for investment accounts, but the route exists
  // for every account type. Portfolio snapshots are a merged view over
  // balance anchors/readings for ANY account id, so rendering this view for
  // a deep-linked depository account would expose its synced balance
  // history to edits/deletes under a "snapshots" label — redirect instead.
  if (account.__typename !== 'InvestmentAccount') {
    return <Navigate to={`/accounts/${account._id}`} replace />;
  }
  return (
    <SnapshotsContent account={account} refetchAccount={refetchAccount} />
  );
};

type SnapshotsContentProps = {
  account: InvestmentAccount;
  refetchAccount: () => void;
};

const SnapshotsContent = ({account, refetchAccount}: SnapshotsContentProps) => {
  const {addSnapshotModal, deleteSnapshotModal, editSnapshotModal} =
    useModals();

  const [beforeCursor, setBeforeCursor] = React.useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = React.useState<string[]>([]);

  // Reset pagination when navigating between accounts
  React.useEffect(() => {
    setBeforeCursor(null);
    setCursorHistory([]);
  }, [account._id]);

  // The light account fragment has no snapshotCount — fetch the full
  // investment fragment for the count shown next to the pager.
  const {data: investmentData, refetch: refetchInvestment} =
    useInvestmentAccountQuery({
      variables: {id: account._id},
      fetchPolicy: 'cache-and-network',
    });
  const snapshotCount = investmentData?.investmentAccount?.snapshotCount;

  const {
    data,
    previousData,
    loading,
    error,
    refetch: refetchSnapshots,
  } = usePortfolioSnapshotsPageQuery({
    variables: {
      accountId: account._id,
      limit: PAGE_SIZE,
      beforeCursor: beforeCursor ?? undefined,
    },
  });

  const page = data?.portfolioSnapshotsPage;
  const items = page?.items ?? previousData?.portfolioSnapshotsPage?.items ?? [];
  const hasNextPage = page?.hasMore ?? false;
  const hasPrevPage = cursorHistory.length > 0;

  const displaySnapshots = useStaleWhileLoading(items, loading);

  const handleNextPage = () => {
    if (page?.endCursor) {
      setCursorHistory((prev) => [...prev, beforeCursor ?? '']);
      setBeforeCursor(page.endCursor);
    }
  };

  const handlePrevPage = () => {
    const prev = cursorHistory[cursorHistory.length - 1];
    setCursorHistory((h) => h.slice(0, -1));
    setBeforeCursor(prev === '' ? null : prev);
  };

  const handleChanged = () => {
    setBeforeCursor(null);
    setCursorHistory([]);
    void refetchSnapshots();
    void refetchInvestment();
    // Snapshots feed Account.balance server-side — keep the header fresh.
    refetchAccount();
  };

  const handleAdd = () => {
    addSnapshotModal.openModal({
      accountId: account._id,
      onSuccess: handleChanged,
    });
  };

  const handleDelete = (snapshot: SnapshotItem) => {
    deleteSnapshotModal.openModal({
      snapshot,
      onDeleted: handleChanged,
    });
  };

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-8">
      <div className="flex items-center justify-between">
        <h3 className="text-foreground m-0 font-medium">
          Portfolio Snapshots
        </h3>
        <div className="flex items-center gap-1">
          {snapshotCount != null && (
            <span className="text-muted-foreground text-sm">
              {snapshotCount} snapshot{snapshotCount !== 1 ? 's' : ''}
            </span>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleAdd}
            title="Add snapshot"
          >
            <Plus className="size-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrevPage}
            disabled={!hasPrevPage || loading}
          >
            <ChevronLeft className="size-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNextPage}
            disabled={!hasNextPage || loading}
          >
            <ChevronRight className="size-5" />
          </Button>
        </div>
      </div>
      {error ? (
        <div className="border-destructive rounded-lg border border-dashed p-6 text-center">
          <p className="text-destructive mb-3 text-sm">
            Failed to load snapshots
          </p>
          <Button variant="outline" onClick={() => void refetchSnapshots()}>
            Retry
          </Button>
        </div>
      ) : displaySnapshots.length === 0 && !loading ? (
        <div className="border-border rounded-lg border border-dashed p-6 text-center">
          <p className="text-muted-foreground mb-3 text-sm">
            No snapshots recorded yet
          </p>
          <Button variant="outline" onClick={handleAdd}>
            Add first snapshot
          </Button>
        </div>
      ) : displaySnapshots.length === 0 && loading ? (
        <div className="text-muted-foreground py-4 text-center text-sm">
          Loading snapshots...
        </div>
      ) : (
        <div
          className={`flex flex-col gap-2 transition-opacity${loading ? ' opacity-50' : ''}`}
        >
          {displaySnapshots.map((snapshot) => (
            <SnapshotRow
              key={snapshot._id}
              snapshot={snapshot}
              onEdit={() =>
                editSnapshotModal.openModal({
                  snapshot,
                  onSuccess: handleChanged,
                })
              }
              onDelete={() => handleDelete(snapshot)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
